import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserFollowedDataObjectGroup.translations.json';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from '../../../common/tanstacktable';
import { Flex } from 'antd';
import styled from 'styled-components';
import { colorBgContainer, colorGray } from '@atlas-frontend/design-tokens';
import {
  DeleteTableRowButton,
  TableOperationButton,
} from '../../../common/form';
import * as Icon from 'react-feather';
import { UserFollowedDataObjectGroupModal } from './UserFollowedDataObjectGroupModal';
import {
  DASHBOARD_GROUP_TABLE_PAGE_SIZE,
  useListUserFollowedDataObjectGroupQuery,
  UserFollowedDataObjectGroupParams,
} from './query/useGetUserFollowedDataObjectGroupQuery';
import { UserFollowedDataObjectGroupQueryDtoType } from './schema/types';
import { EMPTY_PAGE, useMutationErrors } from '../../../common/queryClient';
import { keepPreviousData } from '@tanstack/react-query';
import {
  CustomPagination,
  ErrorAlertWithDrawer,
  InfoIcon,
  InfoIconUrl,
  Padding,
  Stack,
} from '../../../common/components';
import { useMutateUserFollowedDataObjectGroupQuery } from './query/useMutateUserFollowedDataObjectGroupQuery';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useDashBoardSettingsPagination } from '../useDashBoardSettingsPagination';
import { useDebounce } from '../../../common/useDebounce';

const { addTranslations, t } = i18nNsFactory('user-followed-data-object-group');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

const columnHelper =
  createColumnHelper<UserFollowedDataObjectGroupQueryDtoType>();
function getColumns(
  showModal?: () => void,
  onDelete?: (id: number) => void,
): ColumnDef<UserFollowedDataObjectGroupQueryDtoType, any>[] {
  const columns: ColumnDef<UserFollowedDataObjectGroupQueryDtoType, any>[] = [];
  columns.push(
    columnHelper.accessor('dataObjectGroup', {
      header: t(`dataObjectGroup.label`),
    }),
    columnHelper.accessor('periodSkipCount', {
      header: t(`periodSkipCount.label`),
      enableSorting: false,
      enableColumnFilter: false,
      meta: { width: 140 },
    }),
    columnHelper.accessor('operations' as any, {
      header: () => (
        <Flex justify="center" gap="small">
          <TableOperationButton
            id="create-userFollowedDataObjectGroup"
            icon={<Icon.Plus size={22} color={colorGray[7]} />}
            title={t('button.add')}
            onClick={showModal}
          />
        </Flex>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: (props) => (
        <Flex justify="center" gap="small">
          <DeleteTableRowButton
            id={`delete-table-row-${props.row.index}`}
            confirm={{
              onConfirm: () => onDelete?.(props.row.original.id),
            }}
          />
        </Flex>
      ),
      meta: { width: 80 },
    }) as ColumnDef<UserFollowedDataObjectGroupQueryDtoType, any>,
  );

  return columns;
}

export const UserFollowedDataObjectGroup = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const { queryParameters, paginationProps, tableProps, setTotal } =
    useDashBoardSettingsPagination();
  const debouncedParams = useDebounce(queryParameters);

  const {
    data: { content, totalElements } = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useListUserFollowedDataObjectGroupQuery(
    debouncedParams as UserFollowedDataObjectGroupParams,
    {
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  const { mutateAsync } = useMutateUserFollowedDataObjectGroupQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const handleSuccess = useCallback(() => {
    handleClose();
    void refetch();
  }, [handleClose, refetch]);

  const handleDelete = useCallback(
    (id: number) => {
      clearMutationErrors();
      return mutateAsync({ id: id })
        .then(handleSuccess)
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [clearMutationErrors, mutateAsync, handleSuccess, setMutationErrors],
  );

  const columns: ColumnDef<UserFollowedDataObjectGroupQueryDtoType, any>[] =
    useMemo(
      () => getColumns(showModal, handleDelete),
      [handleDelete, showModal],
    );

  return (
    <ContentWrapper size="xs">
      <StyledHeader vertical={12} horizontal>
        <Flex gap="small" align="center">
          <h4>{t('title')}</h4>
          <InfoIcon infoIconUrl={InfoIconUrl.USER_FOLLOWED_DATA_OBJECT_GROUP} />
        </Flex>
        {mutationErrors && (
          <ErrorAlertWithDrawer
            errorAction={t('action')}
            validationMessagesMap={mutationErrors}
          />
        )}
      </StyledHeader>
      <Padding horizontal>
        <TableWrapper id="user-followed-data-object-group-table-wrapper">
          <Table
            name="user-followed-data-object-group-table"
            columns={columns}
            data={content}
            hideClearFilters
            manualFiltering
            manualSorting
            scrollContainerSelector="#user-followed-data-object-group-table-wrapper"
            sticky
            virtual
            columnOrderingDisabled={true}
            columnResizeDisabled={true}
            isLoading={isFetching}
            {...tableProps}
          />
        </TableWrapper>
      </Padding>
      <Padding bottom={12} horizontal>
        <FooterPagination
          {...paginationProps}
          showSizeChanger={false}
          showQuickJumper={false}
          pageSize={DASHBOARD_GROUP_TABLE_PAGE_SIZE}
        />
      </Padding>
      {isModalOpen && (
        <UserFollowedDataObjectGroupModal
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      )}
    </ContentWrapper>
  );
};

const StyledHeader = styled(Padding)`
  border-bottom: 1px solid ${colorGray[2]};
`;

const ContentWrapper = styled(Stack)`
  min-width: 500px;
  background-color: ${colorBgContainer};
  border: 1px solid ${colorGray[2]};
`;

const TableWrapper = styled.div`
  overflow-x: hidden;
  height: 100%;
`;

export const FooterPagination = styled(CustomPagination)`
  float: right;
`;
